import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { TeamsDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DepartmentsByLegalEntity } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/DepartmentsStep/departmentsByLegalEntity'
import { getMatchedLegalEntities } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/helper'
import {
  DatevMapping,
  DatevSource,
  DatevWizardData,
} from 'modules/payroll_integrations_datev_lauds/lib/types'
import { type Action } from 'modules/questionnaires/components/Questionnaire/Actions'
import EmptyState from 'modules/questionnaires/components/Questionnaire/EmptyState'

const datevDepartmentSchema = z.object({
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        datev_id: z.string(),
        meta: z.object({
          legal_entity_id: z.string(),
        }),
      })
    )
    .optional(),
})

const DepartmentsStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const {
    access: { id: accessId },
    company,
  } = useCurrentSession()

  const { navigation } = useNavigation()

  const trackEvent = () => {
    track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsDepartmentsMapped, {
      company_id: company.id,
      access_id: accessId,
      mapped_departments: data?.sources?.departments?.mappings?.length ?? 0,
    })
  }
  const departments: DatevSource = data.sources?.departments || {}

  useUnsafeEffect(() => {
    if (data.sources?.departments === undefined && data.answers.departments !== undefined) {
      const parsedData = datevDepartmentSchema.safeParse(data.answers.departments)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          departments: parsedData.success ? parsedData.data : {},
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        departments: JSON.parse(JSON.stringify(departments)),
      },
    })
  })

  const updateMappings = (newMappings: DatevMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        departments: { mappings: newMappings },
      },
    })
  }

  const { data: teamsData, loading: teamsLoading } = useQuery(TeamsDocument, {})
  const teams = teamsData?.teams.teamsConnection.nodes || []
  const sortedTeams = [...teams].sort((a, b) => a.name.localeCompare(b.name))

  const actions: Action[] = [
    {
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.create_teams_cta'
      ),
      type: 'primary',
      onClick: () => window.open(navigation.employees.teams.fullPath(), '_blank', 'noreferrer'),
      disabled: false,
    },
  ]

  const matchedLegalEntities = getMatchedLegalEntities(data, company)

  if (teamsLoading) {
    return <Spinner />
  }
  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.description'
          )}
        />
        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          {matchedLegalEntities.length > 0 ? (
            matchedLegalEntities.map((legalEntity) => {
              return (
                <DepartmentsByLegalEntity
                  data={data}
                  key={`legal_entity_${legalEntity.id}`}
                  legalEntity={legalEntity}
                  teams={sortedTeams}
                  onMappingChanged={updateMappings}
                />
              )
            })
          ) : (
            <Box maxWidth="s600">
              <EmptyState
                illustration="place"
                title={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.no_teams_available'
                )}
                description={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.no_teams_description'
                )}
                actions={actions}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const departmentsStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.departments.title'
  ),
  nextText: i18n.t('common.save'),
  render: (props) => <DepartmentsStep {...props} />,
  isCompleted: (_data) => true,
})
